// TimeSelector.jsx
import { useState, useRef } from "react";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { Popover, Button, createTheme, ThemeProvider } from "@mui/material";

// 創建深色主題
const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#666666",
    },
    background: {
      paper: "#1A111D",
      default: "#1A111D",
    },
    text: {
      primary: "#CCCCCC",
      secondary: "#999999",
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: "#1A111D",
          color: "#CCCCCC",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          color: "#CCCCCC",
          borderColor: "#666666",
          "&:hover": {
            backgroundColor: "#333333",
            borderColor: "#888888",
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputBase-root": {
            color: "#CCCCCC",
            backgroundColor: "#2A2A2A",
            "& fieldset": {
              borderColor: "#555555",
            },
            "&:hover fieldset": {
              borderColor: "#777777",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#999999",
            },
          },
          "& .MuiInputLabel-root": {
            color: "#999999",
            "&.Mui-focused": {
              color: "#CCCCCC",
            },
          },
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          color: "#CCCCCC",
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: "#333333",
          },
          "&.Mui-selected": {
            backgroundColor: "#666666",
            color: "#FFFFFF",
            "&:hover": {
              backgroundColor: "#777777",
            },
          },
          "&.MuiPickersDay-today": {
            borderColor: "#999999",
          },
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        root: {
          color: "#CCCCCC",
        },
        switchViewButton: {
          color: "#CCCCCC",
          "&:hover": {
            backgroundColor: "#333333",
          },
        },
        leftArrowIcon: {
          color: "#CCCCCC",
        },
        rightArrowIcon: {
          color: "#CCCCCC",
        },
      },
    },
    MuiPickersArrowSwitcher: {
      styleOverrides: {
        button: {
          color: "#CCCCCC",
          "&:hover": {
            backgroundColor: "#333333",
          },
        },
      },
    },
    MuiClock: {
      styleOverrides: {
        root: {
          backgroundColor: "#1A111D",
        },
        clock: {
          backgroundColor: "#2A2A2A",
        },
        pin: {
          backgroundColor: "#666666",
        },
        amButton: {
          color: "#CCCCCC",
          backgroundColor: "#2A2A2A",
          "&.Mui-selected": {
            backgroundColor: "#666666",
            color: "#FFFFFF",
          },
        },
        pmButton: {
          color: "#CCCCCC",
          backgroundColor: "#2A2A2A",
          "&.Mui-selected": {
            backgroundColor: "#666666",
            color: "#FFFFFF",
          },
        },
      },
    },
    MuiClockNumber: {
      styleOverrides: {
        root: {
          color: "#CCCCCC",
          "&.Mui-selected": {
            backgroundColor: "#666666",
            color: "#FFFFFF",
          },
        },
      },
    },
    MuiClockPointer: {
      styleOverrides: {
        root: {
          backgroundColor: "#666666",
        },
        thumb: {
          backgroundColor: "#666666",
          borderColor: "#666666",
        },
      },
    },
    MuiMultiSectionDigitalClock: {
      styleOverrides: {
        root: {
          backgroundColor: "#1A111D",
        },
      },
    },
    MuiMultiSectionDigitalClockSection: {
      styleOverrides: {
        root: {
          "& .MuiMenuItem-root": {
            color: "#CCCCCC",
            "&:hover": {
              backgroundColor: "#333333",
            },
            "&.Mui-selected": {
              backgroundColor: "#666666",
              color: "#FFFFFF",
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: "#CCCCCC",
          "&:hover": {
            backgroundColor: "#333333",
          },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: "#CCCCCC",
        },
      },
    },
  },
});

export default function TimeSelector({ onChange, nowBtn = true }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [value, setValue] = useState(null); // null = 即時資料
  const triggerRef = useRef(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);

    // 使用 setTimeout 確保 Popover 完全關閉後再執行 focus
    setTimeout(() => {
      if (triggerRef.current) {
        triggerRef.current.focus();
      }
    }, 0);
  };

  const handleDateChange = (newValue) => {
    setValue(newValue);
    // 不要立即關閉，讓用戶可以繼續選擇
    // onChange?.(newValue);
  };

  const handleConfirm = () => {
    onChange?.(value);
    handleClose();
  };

  const handleNow = () => {
    setValue(null);
    onChange?.(null);
    handleClose();
  };

  const displayText = value
    ? value.format("YYYY/MM/DD HH:mm:ss")
    : nowBtn
    ? "即時資料"
    : "預設資料";

  return (
    <div>
      {/* 模擬下拉按鈕 */}
      <div
        ref={triggerRef}
        onClick={handleOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleOpen(e);
          }
        }}
        tabIndex={0}
        role="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`時間選擇器，目前選擇：${displayText}`}
        className="bg-[#1A111D]/70 h-[40px] w-[192px] rounded-[12px] px-4 pr-10 text-white text-sm font-medium flex items-center justify-between cursor-pointer relative focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
      >
        {displayText}
        <svg
          className="w-4 h-4 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {/* 彈出 DatePicker */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        slotProps={{
          root: {
            // 移除不必要的 aria-hidden，讓 MUI 自動處理
            "aria-hidden": undefined,
          },
        }}
        PaperProps={{
          sx: {
            backgroundColor: "#1A111D",
            border: "1px solid #333333",
            borderRadius: 2,
            width: 320,
            padding: 2,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.6)",
          },
          role: "dialog",
          "aria-labelledby": "time-selector-label",
        }}
      >
        <ThemeProvider theme={darkTheme}>
          <div id="time-selector-label" className="sr-only">
            時間選擇器
          </div>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              value={value || dayjs()}
              onChange={handleDateChange}
              ampm={false}
              format="YYYY/MM/DD HH:mm:ss"
              maxDateTime={dayjs()} // 限制只能選擇過去時間
              views={["year", "month", "day", "hours", "minutes", "seconds"]} // 明確指定包含秒數
              openTo="day" // 預設開啟到日期選擇
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  "aria-label": "選擇日期時間",
                  sx: {
                    mb: 2,
                  },
                },
                popper: {
                  sx: {
                    "& .MuiPaper-root": {
                      backgroundColor: "#1A111D",
                      border: "1px solid #333333",
                      color: "#CCCCCC",
                    },
                  },
                },
                actionBar: {
                  actions: [], // 隱藏內建的 action bar
                },
              }}
            />
          </LocalizationProvider>

          {/* 操作按鈕 */}
          <div
            className="flex justify-end gap-2"
            role="group"
            aria-label="操作按鈕"
          >
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{
                borderColor: "#555555",
                color: "#999999",
                fontSize: "12px",
                padding: "6px 16px",
                "&:hover": {
                  borderColor: "#777777",
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                },
              }}
            >
              取消
            </Button>
            {nowBtn && (
              <Button
                variant="outlined"
                onClick={handleNow}
                sx={{
                  borderColor: "#555555",
                  color: "#999999",
                  fontSize: "12px",
                  padding: "6px 16px",
                  "&:hover": {
                    borderColor: "#777777",
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                  },
                }}
              >
                現在時間
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={!value}
              sx={{
                backgroundColor: "#666666",
                color: "#FFFFFF",
                fontSize: "12px",
                padding: "6px 16px",
                "&:hover": {
                  backgroundColor: "#777777",
                },
                "&:disabled": {
                  backgroundColor: "#333333",
                  color: "#666666",
                },
              }}
            >
              確認
            </Button>
          </div>
        </ThemeProvider>
      </Popover>
    </div>
  );
}
